/**
 * Single owner of the shared `window.__regard__` namespace.
 *
 * Regard frontends keep every global they manage under one `window.__regard__` object
 * instead of assigning bare top-level keys (`window.VERSION`, …). The namespace is
 * shared across Regard apps and `@rgrd/*` libraries so code and config can move between
 * projects without re-plumbing the global it reads from. A single namespace also
 * minimizes global pollution, gives one place to declare (`global.d.ts`) and clear the
 * globals, and is trivially greppable.
 *
 * Read and write globals only through the helpers here — do not touch `window.__regard__`
 * directly elsewhere, so the namespace has exactly one writer. Because the object is flat
 * and shared, field names are a cross-project contract: keep them specific enough not to
 * collide between apps.
 */

const REGARD_WINDOW_KEY = '__regard__'

/** Returns the Regard namespace if present, or `undefined` (no window, or not yet initialized). */
const getNamespace = (): RegardWindowNamespace | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }
  return window[REGARD_WINDOW_KEY]
}

/**
 * Reads a single field from the Regard window namespace, or `undefined` when it (or the
 * namespace itself) is unset.
 */
export const getRegardWindowValue = <Key extends keyof RegardWindowNamespace>(
  key: Key,
): RegardWindowNamespace[Key] | undefined => getNamespace()?.[key]

/**
 * Writes a single field into the Regard window namespace, creating the namespace object on
 * first write. No-ops when there is no `window` (e.g. server-side or non-DOM contexts).
 */
export const setRegardWindowValue = <Key extends keyof RegardWindowNamespace>(
  key: Key,
  value: RegardWindowNamespace[Key],
): void => {
  if (typeof window === 'undefined') {
    return
  }
  const namespace = window[REGARD_WINDOW_KEY] ?? {}
  namespace[key] = value
  window[REGARD_WINDOW_KEY] = namespace
}

/**
 * Deletes a single field from the Regard window namespace. Primarily for tests that reset
 * global state between cases; no-ops when there is no `window` or no namespace.
 */
export const clearRegardWindowValue = (
  key: keyof RegardWindowNamespace,
): void => {
  const namespace = getNamespace()
  if (namespace) {
    delete namespace[key]
  }
}
